import React from 'react';
import styles from './commonFileUpload.module.scss';

interface CommonFileProgressProps {
  files: File[];
  existingSizeBytes?: number;
  maxSizeMB?: number;
}

const CommonFileProgress: React.FC<CommonFileProgressProps> = ({ files, existingSizeBytes = 0, maxSizeMB = 20 }) => {
  const maxSizeBytes = maxSizeMB * 1024 * 1024;
  const newFilesSizeBytes = files.reduce((acc, f) => acc + f.size, 0);
  const totalSizeBytes = newFilesSizeBytes + existingSizeBytes;
  const remainingBytes = Math.max(0, maxSizeBytes - totalSizeBytes);
  const isOverLimit = totalSizeBytes > maxSizeBytes;
  const percentage = Math.min(100, Math.round((totalSizeBytes / maxSizeBytes) * 100));

  const formatMB = (bytes: number) => {
    return (bytes / (1024 * 1024)).toFixed(2);
  };

  let statusColor = '#10b981'; // Safe (Green)
  let statusBg = '#f0fdf4';
  let statusBorder = '#bbf7d0';
  let iconBg = '#dcfce7';
  let iconClass = 'ri-pie-chart-2-fill'; // Pie chart icon
  let footerIconClass = 'ri-checkbox-circle-fill';

  if (isOverLimit) {
    statusColor = '#ef4444'; // Exceeded (Red)
    statusBg = '#fef2f2';
    statusBorder = '#fecaca';
    iconBg = '#fee2e2';
    footerIconClass = 'ri-close-circle-fill';
  } else if (percentage >= 90) {
    statusColor = '#f97316'; // Near Limit (Orange)
    statusBg = '#fff7ed';
    statusBorder = '#fed7aa';
    iconBg = '#ffedd5';
    footerIconClass = 'ri-error-warning-fill';
  } else if (percentage >= 50) {
    statusColor = '#3b82f6'; // Moderate (Blue)
    statusBg = '#eff6ff';
    statusBorder = '#bfdbfe';
    iconBg = '#dbeafe';
    footerIconClass = 'ri-information-fill';
  }

  return (
    <div 
      className={`d-flex align-items-start gap-3 p-3 rounded mb-3 ${styles.compactProgressCard}`}
      style={{ backgroundColor: statusBg, border: `1px solid ${statusBorder}` }}
    >
      {/* Left Box Icon */}
      <div 
        className="d-flex justify-content-center align-items-center rounded flex-shrink-0" 
        style={{ width: '48px', height: '48px', backgroundColor: iconBg, color: statusColor }}
      >
        <i className={`${iconClass} fs-24`} />
      </div>

      {/* Right Content */}
      <div className="flex-grow-1 w-100">
        
        {/* MB Row */}
        <div className="d-flex justify-content-between align-items-center mb-1">
          <div className="fw-bold" style={{ fontSize: 13 }}>
            <span style={{ color: statusColor }}>{formatMB(totalSizeBytes)} MB</span>
            <span className="text-muted"> / {maxSizeMB} MB</span>
          </div>
          <div className="fw-bold" style={{ fontSize: 12, color: statusColor }}>
            {isOverLimit 
              ? `${formatMB(totalSizeBytes - maxSizeBytes)} MB over limit` 
              : `${formatMB(remainingBytes)} MB remaining`
            }
          </div>
        </div>

        {/* Progress Bar Row */}
        <div className={styles.progressBarWrapper}>
          <div className={styles.progressTrackStyle}>
            <div 
              className={styles.progressFillStyle} 
              style={{ 
                width: `${isOverLimit ? 100 : percentage}%`,
                backgroundColor: statusColor
              }} 
            />
          </div>
        </div>
        
      </div>
    </div>
  );
};

export default CommonFileProgress;
